import { LoadingSkeleton } from '@/components/LoadingSkeleton/LoadingSkeleton'

/** One skeleton column: a fixed pixel width and the real header label (empty label renders no shimmer). */
export type MatterRecordSkeletonColumn = { width: number; label: string }

/**
 * Skeleton table shown while the first asset build or events load is in flight
 * (Figma: Table/MedChron/Loading, node 8744:82715). Real column headers stay
 * visible during extraction — matching the live events table (MatterRecordEventsTable) —
 * so the loading state reads as the same table and columns don't shift when data
 * lands. Only the body cells shimmer. Subsequent updates keep the live table visible.
 *
 * Columns are passed in by the feature so the skeleton stays aligned with the live
 * table's column set (kept aligned avoids the layout shift when data lands).
 */
export default function MatterRecordProcessingSkeleton({ columns }: { columns: MatterRecordSkeletonColumn[] }) {
  // Pin each cell to its exact column width (width alone lets flex shrink header and body
  // cells by different amounts — header text floors at its min-content while shimmer cells
  // shrink freely — drifting the column borders apart on narrow containers).
  const cellWidth = (col: MatterRecordSkeletonColumn) => ({ width: `${col.width}px`, minWidth: `${col.width}px`, maxWidth: `${col.width}px` })

  return (
    <div className="flex-1 min-h-0 overflow-hidden p-2 lg:p-4">
      {/* Scrolls horizontally when the pinned columns exceed the container, like the live table. */}
      <div className="border rounded-md bg-white overflow-x-auto">
        {/* Header row — real column labels (matches the live table header). */}
        <div className="flex border-b bg-brand-neutral-50">
          {columns.map((col, index) => (
            <div
              key={`header-${index}`}
              className="flex min-h-[42px] flex-col justify-center px-4 py-2 border-r text-left text-sm font-medium text-black tracking-[-0.15px]"
              style={cellWidth(col)}
            >
              {col.label}
            </div>
          ))}
          {/* Spacer fills leftover width on wide viewports, like the live table's spacer column. */}
          <div className="flex-1" />
        </div>
        {/* Body rows — Figma's loading table runs 13 rows past the fold rather than stopping short. */}
        {Array.from({ length: 13 }).map((_, rowIndex) => (
          <div key={`row-${rowIndex}`} className="flex border-b last:border-b-0">
            {columns.map((col, cellIndex) => (
              <div key={`cell-${rowIndex}-${cellIndex}`} className="flex h-[72px] items-center px-4 py-2 border-r" style={cellWidth(col)}>
                {col.label === '' ? null : <LoadingSkeleton width="100%" lineHeight={12} />}
              </div>
            ))}
            <div className="flex-1" />
          </div>
        ))}
      </div>
    </div>
  )
}
